import type { LevelDefinition } from '../data/levels';
import type { Rank, RunResult, ScoreState } from './types';

export class ScoreSystem {
  readonly state: ScoreState = {
    score: 0,
    combo: 0,
    comboTicks: 0,
    maxCombo: 0,
    damageTaken: 0,
    stunCount: 0,
    nearMisses: 0,
    shardFound: false,
    extractionTicks: 0,
  };

  tick(): void {
    if (this.state.comboTicks > 0) this.state.comboTicks -= 1;
    if (this.state.comboTicks === 0) this.state.combo = 0;
  }

  collectCore(): number {
    this.state.combo = Math.min(9, this.state.combo + 1);
    this.state.comboTicks = 150;
    this.state.maxCombo = Math.max(this.state.maxCombo, this.state.combo);
    // Dense fruit families reward flow without making rank thresholds
    // automatic from pickup volume alone.
    const points = 100 + this.state.combo * 25;
    this.state.score += points;
    return points;
  }

  completeFruitWave(waveIndex: number): number {
    const points = 600 + waveIndex * 250;
    this.state.score += points;
    this.state.comboTicks = Math.max(this.state.comboTicks, 210);
    return points;
  }

  stunEnemy(energized: boolean): number {
    this.state.stunCount += 1;
    const points = energized ? 1_250 : 650;
    this.state.score += points + this.state.combo * 50;
    this.state.combo = Math.min(9, this.state.combo + 1);
    this.state.comboTicks = 180;
    return points;
  }

  nearMiss(): void {
    this.state.nearMisses += 1;
    this.state.score += 180;
  }

  damage(): void {
    this.state.damageTaken += 1;
    this.state.combo = 0;
    this.state.comboTicks = 0;
  }

  shard(): void {
    if (this.state.shardFound) return;
    this.state.shardFound = true;
    this.state.score += 2_000;
  }

  finish(level: LevelDefinition, elapsedTicks: number): RunResult {
    const timeMs = Math.round(elapsedTicks * (1000 / 60));
    const speedBonus = Math.max(0, Math.round((level.targetTimeMs - timeMs) / 25));
    const noDamageBonus = this.state.damageTaken === 0 ? 1_500 : 0;
    const extractionBonus = Math.max(0, 1_500 - this.state.extractionTicks * 3);
    this.state.score += speedBonus + noDamageBonus + extractionBonus;
    return {
      levelId: level.id,
      timeMs,
      score: this.state.score,
      rank: calculateRank(this.state.score, level.ranks),
      speedMedal: timeMs <= level.targetTimeMs,
      noDamageMedal: this.state.damageTaken === 0,
      shardMedal: this.state.shardFound,
      maxCombo: this.state.maxCombo,
    };
  }
}

export function calculateRank(score: number, thresholds: LevelDefinition['ranks']): Rank {
  if (score >= thresholds.S) return 'S';
  if (score >= thresholds.A) return 'A';
  if (score >= thresholds.B) return 'B';
  return 'C';
}
